import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import cx from 'classnames';
const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: 98,
    margin: '0 auto',
    gap: 8,
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center',
    },
  },
  hasSelector: {
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      top: -6,
    },
  },
  value: {
    color: '#31415E',

    flex: 1,
    display: 'flex',
    gap: 8,
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  },
  input: {
    border: '1px solid #E1E4EB',
    outline: 'none',
    minWidth: 40,
    textAlign: 'center',
    width: '100%',
    color: '#31415E',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    background: 'transparent',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '-moz-appearance': 'textfield',
  },
  icon: {
    cursor: 'pointer',
    flex: 1,
  },
  iconWrapper: {
    width: 30,
    flex: 1,
  },
  fontSizeLarger: {
    fontSize: 20,
    position: 'relative',
    top: -2,
  },
});

const QuantitySelector = ({
  fontSize = 'normal',
  classes,
  startingQuantity = 1,
  value,
  onQtyChanged = null,
  loading,
  isAllowToChangeQuantity = false,
  limit_qty_per_customer = 0,
  min_qty_per_customer = 0,
}) => {
  const [qty, setQty] = useState(value);
  const [isAllowToMinus, setIsAllowToMinus] = useState(true);
  const [isAllowToAdd, setIsAllowToAdd] = useState(true);

  const minusQty = useCallback(() => {
    if (!loading) {
      if (qty > startingQuantity) {
        const newQty = qty - 1;
        setQty(newQty);
        if (typeof onQtyChanged === 'function') {
          onQtyChanged(newQty);
        }
      }
    }
  }, [qty, onQtyChanged, loading, startingQuantity]);

  const addQty = useCallback(() => {
    if (!loading) {
      const newQty = qty + 1;
      setQty(newQty);
      if (typeof onQtyChanged === 'function') {
        onQtyChanged(newQty);
      }
    }
  }, [qty, onQtyChanged, loading]);

  const handleInputChange = useCallback(
    e => {
      if (!loading) {
        const inputValue = e.target.value;
        // Allow empty string for user to clear and type
        if (inputValue === '') {
          setQty('');
          return;
        }

        const numValue = parseInt(inputValue, 10);

        // Validate the input
        if (!isNaN(numValue) && numValue >= startingQuantity) {
          // Check upper limit if it exists
          if (limit_qty_per_customer && numValue > limit_qty_per_customer) {
            setQty(limit_qty_per_customer);
            if (typeof onQtyChanged === 'function') {
              onQtyChanged(limit_qty_per_customer);
            }
          } else {
            setQty(numValue);
            if (typeof onQtyChanged === 'function') {
              onQtyChanged(numValue);
            }
          }
        }
      }
    },
    [loading, startingQuantity, limit_qty_per_customer, onQtyChanged]
  );

  const handleInputBlur = useCallback(() => {
    // If input is empty or invalid on blur, reset to starting quantity
    if (qty === '' || qty < startingQuantity) {
      setQty(startingQuantity);
      if (typeof onQtyChanged === 'function') {
        onQtyChanged(startingQuantity);
      }
    }
  }, [qty, startingQuantity, onQtyChanged]);

  useEffect(() => {
    setQty(value);
  }, [value]);

  useEffect(() => {
    if (min_qty_per_customer) {
      if (qty - startingQuantity >= min_qty_per_customer) {
        setIsAllowToMinus(true);
      } else {
        setIsAllowToMinus(false);
      }
    } else {
      if (qty === startingQuantity) {
        setIsAllowToMinus(false);
      } else {
        setIsAllowToMinus(true);
      }
    }
    if (limit_qty_per_customer) {
      if (qty + startingQuantity <= limit_qty_per_customer) {
        setIsAllowToAdd(true);
      } else {
        setIsAllowToAdd(false);
      }
    }
  }, [min_qty_per_customer, qty, limit_qty_per_customer, startingQuantity]);

  return (
    <div
      className={cx(classes.root, {
        [classes.hasSelector]: (isAllowToMinus || isAllowToAdd) && isAllowToChangeQuantity === true,
      })}
    >
      {isAllowToChangeQuantity && (
        <div className={classes.iconWrapper}>
          {isAllowToMinus && (
            <img src="/static/images/minus.svg" className={classes.icon} onClick={minusQty} />
          )}
        </div>
      )}
      <div className={cx(classes.value, { [classes.fontSizeLarger]: fontSize === 'large' })}>
        x{' '}
        <input
          type="number"
          className={classes.input}
          value={qty || 0}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={loading || !isAllowToChangeQuantity}
          min={startingQuantity}
          max={limit_qty_per_customer || undefined}
        />
      </div>
      {isAllowToChangeQuantity && (
        <div className={classes.iconWrapper}>
          {isAllowToAdd && (
            <img src="/static/images/add.svg" className={classes.icon} onClick={addQty} />
          )}
        </div>
      )}
    </div>
  );
};

QuantitySelector.propTypes = {
  fontSize: PropTypes.string,
  classes: PropTypes.object.isRequired,
  value: PropTypes.number,
  startingQuantity: PropTypes.number,
  onQtyChanged: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isAllowToChangeQuantity: PropTypes.bool,
  limit_qty_per_customer: PropTypes.number,
  min_qty_per_customer: PropTypes.number,
};
export default withStyles(styles)(QuantitySelector);
